import React, { useState } from 'react';
import { ArrowLeft, Play, Mic, X } from './Icons';
import { tutorVoiceSelectionData } from '../data/tutorVoiceSelectionData';

const TutorVoiceSelectionPage = ({ onBack, onSave, userPreferences }) => {
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [playingVoice, setPlayingVoice] = useState(null);

  const { displayLanguage } = userPreferences;
  const data = tutorVoiceSelectionData;

  const getText = (textObj) => {
    return textObj[displayLanguage] || textObj.en;
  };

  const handleVoiceSelect = (voiceId) => {
    setSelectedVoice(voiceId);
  };

  const handlePlayPreview = (voiceId) => {
    setPlayingVoice(voiceId);
    // Simulate audio playback
    setTimeout(() => {
      setPlayingVoice(null);
    }, 3000);
  };

  const handleSave = () => {
    if (selectedVoice) {
      onSave(selectedVoice);
    }
  };

  const getGenderIcon = (gender) => {
    return gender === 'female' ? '♀' : '♂';
  };

  const getGenderColor = (gender) => {
    return gender === 'female' ? 'bg-pink-500' : 'bg-blue-500';
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <div className="w-6"></div> {/* Placeholder for alignment */}
          <h1 className="text-xl font-bold">{getText(data.overview.title)}</h1>
          <button 
            onClick={onBack}
            className="flex items-center text-white hover:text-blue-300 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Voice Options */}
        <div className="px-6 pb-20">
          <div className="space-y-3">
          {data.voiceOptions.voices.map((voice) => (
            <div 
              key={voice.id}
              onClick={() => handleVoiceSelect(voice.id)}
              className={`bg-gray-800/50 rounded-xl p-4 border transition-all duration-200 cursor-pointer ${
                selectedVoice === voice.id 
                  ? 'border-purple-500 bg-purple-500/20' 
                  : 'border-gray-600 hover:border-gray-500'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  {/* Flag Icon */}
                  <div className="text-2xl">{voice.flag}</div>
                  
                  {/* Voice Info */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold">{getText(voice.accentName)}</h3>
                      <div className={`w-4 h-4 ${getGenderColor(voice.gender)} rounded-full flex items-center justify-center text-xs text-white`}>
                        {getGenderIcon(voice.gender)}
                      </div>
                    </div>
                    <p className="text-sm text-gray-300">{getText(voice.characteristics)}</p>
                  </div>
                </div>
                
                {/* Play Button */}
                <button
                  onClick={() => handlePlayPreview(voice.id)}
                  disabled={playingVoice === voice.id}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    playingVoice === voice.id 
                      ? 'bg-gray-600 cursor-not-allowed' 
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  {playingVoice === voice.id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Play size={16} className="text-white ml-0.5" />
                  )}
                </button>
              </div>
              
              {/* Selection Indicator */}
              {selectedVoice === voice.id && (
                <div className="mt-3 pt-3 border-t border-purple-500/30">
                  <div className="flex items-center space-x-2 text-purple-300">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="text-sm">已選擇</span>
                  </div>
                </div>
              )}
            </div>
          ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="sticky bottom-0 p-6 bg-gradient-to-t from-purple-900 to-transparent">
          <button
          onClick={handleSave}
          disabled={!selectedVoice}
          className={`w-full py-4 rounded-xl text-white font-semibold transition-colors ${
            selectedVoice 
              ? 'bg-amber-600 hover:bg-amber-700' 
              : 'bg-gray-600 cursor-not-allowed'
          }`}
        >
            {getText(data.saveButton.title)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorVoiceSelectionPage;
